import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/utils/axios";
import {
  ProviderCredential,
  ProviderCredentialRequest,
  ProviderCredentialResponse,
  ProviderCredentialTestResponse,
} from "@/types/providercredentails";
import type { AxiosError } from "axios";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Custom hook for provider credentials:
 * - lists existing credentials
 * - tests a new credential (without persisting)
 * - sets up a new credential (persists and invalidates cache)
 */
export function useProviderCredentials() {
  const queryClient = useQueryClient();
  const { isAuthenticated, token, isLoading: authLoading } = useAuth();

  // List existing credentials
  const credentialsQuery = useQuery<ProviderCredential[], Error>({
    queryKey: ["providerCredentials"],
    queryFn: async () => {
      const response = await api.get<ProviderCredential[]>(
        "/v1/provider-credentials/"
      );
      return response.data;
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    enabled: isAuthenticated && !!token && !authLoading,
  });

  // Test connection using dedicated test endpoint without persisting
  const testConnection = useMutation<
    ProviderCredentialTestResponse,
    AxiosError,
    ProviderCredentialRequest
  >({
    mutationFn: (req) =>
      api
        .post<ProviderCredentialTestResponse>(
          "/v1/provider-credentials/test",
          req
        )
        .then((res) => res.data),
    onSuccess: (_data, variables) => {
      // Store last validation time locally per provider for expiry reminder
      const key =
        variables.provider === "azure" ? "azure-last-valid" : "gcp-last-valid";
      sessionStorage.setItem(key, new Date().toISOString());
    },
  });

  // Setup and persist credential, then invalidate list cache
  const setupCredential = useMutation<
    ProviderCredentialResponse,
    AxiosError,
    ProviderCredentialRequest
  >({
    mutationFn: (req) =>
      api
        .post<ProviderCredentialResponse>("/v1/provider-credentials/", req)
        .then((res) => res.data),
    onSuccess: (_data, variables) => {
      const key =
        variables.provider === "azure" ? "azure-last-valid" : "gcp-last-valid";
      sessionStorage.setItem(key, new Date().toISOString());
      queryClient.invalidateQueries({ queryKey: ["providerCredentials"] });
    },
  });

  return {
    // query state: data, isLoading, error, etc.
    ...credentialsQuery,
    // mutation for testing connection
    testConnection,
    // mutation for setup (persist)
    setupCredential,
  };
}
